import type { ReactNode } from 'react';
import { connect } from 'react-redux';
import { getQuoteType } from 'state/quotes/selectors';
import {
  confirmedExecutionOnlyDealId,
  tradeExecutionCommentChanged,
  tradeNotificationContactsChanged,
} from 'state/quotes/trades/trades.action';
import {
  getFormEnrichedData,
  getFormInfos,
  getFormQuoteResult,
  getFormRequestType,
  getIsUserInternal,
  getTradeConfirmation,
} from 'state/selectors';
import { getTradeInfosByWorkspaceTab } from 'state/selectors/common/temp';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import {
  confirmedFrenchPublicOffering,
  confirmedKidDocumentation,
  confirmedTermsheetDisclaimer,
} from 'state/uiPanelState/tradeConfirmation/tradeConfirm.action';
import type { QuoteWorkspaceType } from 'state/workspaces/model';

export interface PublicOffering {
  PublicOfferingCountry: string;
  PublicOfferingStartDate: Date;
  PublicOfferingEndDate: Date;
}

export interface OwnProps {
  tabId: string;
  workspaceType: QuoteWorkspaceType;
  termsheetButton: ReactNode;
  className?: string;
}

export interface StateProps {
  quoteId: string;
  haveKidDocumentation: boolean;
  haveFrenchPublicOffering: boolean;

  hasConfirmedTermsheetDisclaimer: boolean;
  hasConfirmedKidDocumentation: boolean;
  hasConfirmedFrenchPublicOffering: boolean;
  hadTraded: boolean;
  executionOnly: boolean;
  isExecutionOnlyAddon: boolean;

  executionOnlyDealId: string;
  comment: string;
  canComment: boolean;
}

export interface DispatchProps {
  onTermsheetConfirmed(quoteId: string): (checked: boolean) => void;

  onKidDocumentationConfirmed(quoteId: string): (checked: boolean) => void;

  onFrenchPublicOfferingConfirmed(quoteId: string): (checked: boolean) => void;

  onExecutionOnlyCommentConfirmed(quoteId: string): (comment: string) => void;
  onExecutionOnlyDealIdConfirmed(quoteId: string): (dealId: string) => void;
  onTradeNotificationContactsChanged(quoteId: string): (contactIds: string[]) => void;
}

export type TradeActionsProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps = (state: AppState, { tabId, workspaceType }: OwnProps): StateProps => {
  const formEnrichedData = getFormEnrichedData(state, tabId);
  const { quoteId } = getFormInfos(state, workspaceType, tabId)!;

  const { executionOnlyDealId, comment } = getTradeInfosByWorkspaceTab(state, workspaceType, tabId);
  const quoteType = getQuoteType(state, quoteId);
  const executionOnly = quoteType === 'ExecOnly' || quoteType === 'AdvancedExecOnly';
  const isExecutionOnlyAddon = getFormQuoteResult(state, workspaceType, tabId)?.isExecOnlyAddon ?? false;
  const confirmation = getTradeConfirmation(state, workspaceType, tabId);
  const publicOffering = Array.isArray(formEnrichedData.PublicOffering)
    ? (formEnrichedData.PublicOffering as PublicOffering[])
    : undefined;
  const haveFrenchPublicOffering = publicOffering?.some(po => po.PublicOfferingCountry === 'FRA') ?? false;
  const haveKidDocumentation = formEnrichedData.KidRequired as boolean;
  const hasConfirmedFrenchPublicOffering =
    !haveFrenchPublicOffering || (confirmation?.hasConfirmedFrenchPublicOffering ?? false);
  const hasConfirmedTermsheetDisclaimer = confirmation?.hasConfirmedTermsheetDisclaimer ?? false;
  const hasConfirmedKidDocumentation = !haveKidDocumentation || (confirmation?.hasConfirmedKidDocumentation ?? false);
  const hadTraded = confirmation?.hadTraded ?? false;
  const canComment = getIsUserInternal(state) && getFormRequestType(state, workspaceType, tabId) !== 'RequestForStream';

  return {
    quoteId: quoteId!,
    haveKidDocumentation,
    haveFrenchPublicOffering,
    hasConfirmedFrenchPublicOffering,
    hasConfirmedKidDocumentation,
    hasConfirmedTermsheetDisclaimer,
    hadTraded,
    executionOnly,
    isExecutionOnlyAddon,
    comment,
    canComment,
    executionOnlyDealId,
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch): DispatchProps => ({
  onTermsheetConfirmed(quoteId: string) {
    return (checked: boolean) => {
      dispatch(confirmedTermsheetDisclaimer(quoteId, checked));
    };
  },
  onKidDocumentationConfirmed(quoteId: string) {
    return (checked: boolean) => {
      dispatch(confirmedKidDocumentation(quoteId, checked));
    };
  },
  onFrenchPublicOfferingConfirmed(quoteId: string) {
    return (checked: boolean) => {
      dispatch(confirmedFrenchPublicOffering(quoteId, checked));
    };
  },

  onExecutionOnlyCommentConfirmed(quoteId: string) {
    return (comment: string) => {
      dispatch(tradeExecutionCommentChanged(quoteId, comment));
    };
  },

  onExecutionOnlyDealIdConfirmed(quoteId: string) {
    return (dealId: string) => {
      dispatch(confirmedExecutionOnlyDealId(quoteId, dealId));
    };
  },
  onTradeNotificationContactsChanged(quoteId: string) {
    return (contactIds: string[]) => {
      dispatch(tradeNotificationContactsChanged(quoteId, contactIds));
    };
  },
});

export const connectTradeAction = connect(mapStateToProps, mapDispatchToProps);
